// Pure helpers for order device/quantity handling in OrderDetails.
// Quantities are keyed by device_uuid; a value is either a number (plain
// device) or an object keyed by variant label (SmartMeter variant device).

export const VARIANT_NOT_YET_SELECTED = 'VARIANT_NOT_YET_SELECTED';

export const getQuantitiesFromOrderItems = orderItems => (
  orderItems?.reduce((acc, item) => {
    if (item.variant_label) {
      acc[item.device_uuid] = acc[item.device_uuid] || {};
      acc[item.device_uuid][item.variant_label] = item.quantity;
    } else {
      acc[item.device_uuid] = item.quantity;
    }
    return acc;
  }, {}) || {}
);

export const sumQuantity = q => {
  if (typeof q === 'number') {
    return q;
  }
  if (q && typeof q === 'object') {
    return Object.values(q).reduce((acc, v) => acc + (v || 0), 0);
  }
  return 0;
};

export const hasPositiveQuantity = quantities =>
  Object.values(quantities || {}).some(q => sumQuantity(q) > 0);

const makePlaceholderDevice = storedItem => ({
  device_uuid: storedItem.device_uuid,
  device_type: storedItem.device_type || 'Other',
  device_manufacturer: storedItem.device_manufacturer || '',
  device_model: storedItem.device_model || storedItem.device_uuid,
  unavailable: true,
});

// Reconciles the fetched catalog with the order's stored items at the
// (device_uuid, variant_label) level. Stored lines whose device or variant is
// no longer in the catalog stay visible as "unavailable" so they are never
// silently dropped; picker selections that are neither stored nor in the new
// catalog are removed so quantities can't hold orphan keys.
export function mergeCatalogWithStoredItems({ catalogDevices, storedOrderItems, prevQuantities }) {
  const catalog = catalogDevices || [];
  const stored = storedOrderItems || [];
  const prev = prevQuantities || {};
  const storedQuantities = getQuantitiesFromOrderItems(stored);

  const orderableDevices = [];
  const quantities = {};
  const unavailableLines = [];

  catalog.forEach(d => {
    const storedQ = storedQuantities[d.device_uuid];
    const prevQ = prev[d.device_uuid];
    const hasVariants = d.smartmeter_variant_required && d.smartmeter_variants?.length > 0;
    if (hasVariants) {
      const currentLabels = d.smartmeter_variants.map(v => v.label);
      const q = {};
      currentLabels.forEach(label => {
        q[label] = (typeof prevQ === 'object' ? prevQ?.[label] : undefined)
          ?? (typeof storedQ === 'object' ? storedQ?.[label] : undefined)
          ?? 0;
      });
      // A persisted plain line is not the same order item as any currently
      // offered variant. Keep it under the no-label sentinel as an unavailable
      // line so it can be decreased or removed without silently relabeling it.
      const prevHasPlainLine = typeof prevQ === 'number'
        || (prevQ && typeof prevQ === 'object'
          && Object.prototype.hasOwnProperty.call(prevQ, VARIANT_NOT_YET_SELECTED));
      // The variant picker moves the plain quantity onto a variant and deletes
      // the sentinel key: an object without it has no plain line left to carry.
      const numericCarry = typeof storedQ === 'number'
        ? (prevHasPlainLine
          ? (typeof prevQ === 'number' ? prevQ : prevQ[VARIANT_NOT_YET_SELECTED])
          : (prevQ && typeof prevQ === 'object' ? undefined : storedQ))
        : undefined;
      const goneLabels = [];
      if (numericCarry !== undefined) {
        q[VARIANT_NOT_YET_SELECTED] = numericCarry;
      }
      if (numericCarry > 0) {
        goneLabels.push(VARIANT_NOT_YET_SELECTED);
        unavailableLines.push({ device_uuid: d.device_uuid, variant_label: null });
      }
      Object.entries(typeof storedQ === 'object' ? storedQ : {}).forEach(([label, qty]) => {
        if (!currentLabels.includes(label)) {
          q[label] = (typeof prevQ === 'object' ? prevQ?.[label] : undefined) ?? qty;
          goneLabels.push(label);
          unavailableLines.push({ device_uuid: d.device_uuid, variant_label: label });
        }
      });
      orderableDevices.push(goneLabels.length > 0 ? { ...d, unavailableVariants: goneLabels } : d);
      quantities[d.device_uuid] = q;
    } else if (typeof storedQ === 'object') {
      // Device switched from variant-required to plain: keep the stored
      // variant lines untouched (decrease-only) instead of coercing them.
      const goneLabels = Object.keys(storedQ);
      const q = {};
      goneLabels.forEach(label => {
        q[label] = (typeof prevQ === 'object' ? prevQ?.[label] : undefined) ?? storedQ[label];
        unavailableLines.push({ device_uuid: d.device_uuid, variant_label: label });
      });
      // The current catalog also offers a valid plain line. The sentinel lets
      // that line coexist with historical variant rows until they are removed.
      q[VARIANT_NOT_YET_SELECTED] = (typeof prevQ === 'object'
        ? prevQ?.[VARIANT_NOT_YET_SELECTED]
        : undefined) ?? 0;
      orderableDevices.push({ ...d, unavailableVariants: goneLabels });
      quantities[d.device_uuid] = q;
    } else {
      orderableDevices.push(d);
      quantities[d.device_uuid] = (typeof prevQ === 'number' ? prevQ : undefined) ?? storedQ ?? 0;
    }
  });

  const catalogUuids = new Set(catalog.map(d => d.device_uuid));
  const placeholderByUuid = {};
  stored.forEach(item => {
    if (catalogUuids.has(item.device_uuid) || placeholderByUuid[item.device_uuid]) {
      return;
    }
    placeholderByUuid[item.device_uuid] = makePlaceholderDevice(item);
    const storedQ = storedQuantities[item.device_uuid];
    const prevQ = prev[item.device_uuid];
    if (typeof storedQ === 'object') {
      const q = {};
      Object.keys(storedQ).forEach(label => {
        q[label] = (typeof prevQ === 'object' ? prevQ?.[label] : undefined) ?? storedQ[label];
        unavailableLines.push({ device_uuid: item.device_uuid, variant_label: label });
      });
      quantities[item.device_uuid] = q;
    } else {
      quantities[item.device_uuid] = (typeof prevQ === 'number' ? prevQ : undefined) ?? storedQ ?? 0;
      unavailableLines.push({ device_uuid: item.device_uuid, variant_label: null });
    }
  });
  orderableDevices.push(...Object.values(placeholderByUuid));

  return { orderableDevices, quantities, unavailableLines };
}

// Display projection for stored items when the catalog request failed: the
// devices may well still be orderable, so they are "availability unknown"
// (edits disabled) rather than "unavailable" (decrease-only).
export function buildUnknownAvailabilityDevices(storedOrderItems) {
  const byUuid = {};
  (storedOrderItems || []).forEach(item => {
    if (!byUuid[item.device_uuid]) {
      byUuid[item.device_uuid] = { ...makePlaceholderDevice(item), unavailable: false, availabilityUnknown: true };
    }
  });
  return Object.values(byUuid);
}

export function buildOrderItemsFromQuantities(orderableDevices, quantities) {
  const items = [];
  (orderableDevices || []).forEach(d => {
    const q = (quantities || {})[d.device_uuid];
    if (q && typeof q === 'object') {
      Object.entries(q).forEach(([label, qty]) => {
        if (qty > 0) {
          items.push({
            device_uuid: d.device_uuid,
            quantity: qty,
            variant_label: label === VARIANT_NOT_YET_SELECTED ? undefined : label,
          });
        }
      });
    } else if (q > 0) {
      items.push({ device_uuid: d.device_uuid, quantity: q });
    }
  });
  return items;
}

export function buildCurrentOrderItems({ hasShipments, isReturnFlow, originalItems, orderableDevices, quantities }) {
  if (hasShipments || isReturnFlow) {
    return [...(originalItems || [])];
  }
  return buildOrderItemsFromQuantities(orderableDevices, quantities);
}

const isUnavailableLine = (device, variantLabel) => {
  if (!device) {
    return false;
  }
  if (device.unavailable) {
    return true;
  }
  return Boolean(variantLabel && device.unavailableVariants?.includes(variantLabel));
};

// Unavailable lines may be decreased or removed, never increased above what
// the order already holds. Available lines are unclamped.
export function clampQuantity({ device, storedQuantities, variant, requested }) {
  const bounded = Math.max(0, requested);
  if (!isUnavailableLine(device, variant)) {
    return bounded;
  }
  const storedQ = storedQuantities?.[device.device_uuid];
  const ceiling = variant
    ? (variant === VARIANT_NOT_YET_SELECTED && typeof storedQ === 'number'
      ? storedQ
      : (typeof storedQ === 'object' ? storedQ?.[variant] : undefined) ?? 0)
    : (typeof storedQ === 'number' ? storedQ : 0);
  return Math.min(bounded, ceiling);
}

// Devices still carrying quantity on an unavailable line; a non-empty result
// blocks save/accept until the lines are removed or zeroed.
export function getUnavailableItemsInOrder(orderableDevices, quantities) {
  return (orderableDevices || []).filter(d => {
    const q = (quantities || {})[d.device_uuid];
    if (d.unavailable) {
      return sumQuantity(q) > 0;
    }
    if (d.unavailableVariants?.length > 0 && q && typeof q === 'object') {
      return d.unavailableVariants.some(label => q[label] > 0);
    }
    return false;
  });
}

// One row per device/variant line for the list view; variant devices render a
// row per quantity key so unavailable variant lines stay editable there too.
export function flattenDevicesToRows(orderableDevices, quantities) {
  const rows = [];
  (orderableDevices || []).forEach(d => {
    const q = (quantities || {})[d.device_uuid];
    if (q && typeof q === 'object') {
      Object.keys(q).forEach(label => {
        rows.push({
          device: d,
          variant: { label },
          unavailable: isUnavailableLine(d, label),
          quantity: q[label] || 0,
        });
      });
    } else {
      rows.push({
        device: d,
        variant: null,
        unavailable: Boolean(d.unavailable),
        quantity: q || 0,
      });
    }
  });
  return rows;
}
